import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "../lib/api";
import StatusBadge from "../components/StatusBadge";
import ScoreRadial, { ScoreBar } from "../components/ScoreRadial";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

export default function CandidateDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("overview");

  // Schedule modal
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleRound, setScheduleRound] = useState("round2");
  const [isReschedule, setIsReschedule] = useState(false);
  const [scheduleAt, setScheduleAt] = useState("");
  const [meetingLink, setMeetingLink] = useState("");
  const [panel, setPanel] = useState("");
  const [scheduleNotes, setScheduleNotes] = useState("");

  // Round 1 (HR Screening) feedback
  const decisionInFlight = useRef(false);
  const [r1, setR1] = useState({
    communication: 3, role_fit: 3, stability: 3,
    strengths: "", concerns: "", recommendation: "proceed", comments: "",
    ctc_current_monthly: "", ctc_expected_monthly: "",
    notice_period: "", marital_status: "",
    current_location: "", hometown: "",
    relocation: "", career_gap: "", career_gap_reason: "",
    asset_allocation: "", job_change_reason: "",
  });
  // Round 2 feedback
  const [r2, setR2] = useState({ technical_fit: 3, communication: 3, culture_fit: 3, strengths: "", concerns: "", recommendation: "proceed", comments: "" });
  // Final feedback
  const [ff, setFf] = useState({ technical_depth: 3, leadership: 3, behavioral_fit: 3, salary_fit: 3, overall: "select", comments: "" });

  // HR decision
  const [decisionComment, setDecisionComment] = useState("");

  // Offer
  const [offer, setOffer] = useState({ offered_ctc: "", joining_date: "", documents_checklist: "Aadhaar/PAN, Last 3 months payslips, Education docs, Offer signed", notes: "" });

  const reload = async () => {
    const r = await api.get(`/candidates/${id}`);
    setC(r.data);
  };

  // Build a Blob URL for the resume so it can be embedded / downloaded.
  const resumeUrl = useMemo(() => {
    if (!c?.resume_file_b64) return null;
    try {
      const bin = atob(c.resume_file_b64);
      const arr = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
      const ext = (c.resume_filename || "").toLowerCase().split(".").pop();
      const mime =
        ext === "pdf"
          ? "application/pdf"
          : ext === "docx"
          ? "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          : ext === "doc"
          ? "application/msword"
          : "application/octet-stream";
      const blob = new Blob([arr], { type: mime });
      return { url: URL.createObjectURL(blob), mime, ext };
    } catch {
      return null;
    }
  }, [c?.resume_file_b64, c?.resume_filename]);

  const downloadResume = () => {
    if (!resumeUrl) return;
    const a = document.createElement("a");
    a.href = resumeUrl.url;
    a.download = c.resume_filename || "resume";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!c) return <div className="p-8 text-quiet">Loading…</div>;

  const score = c.ai_score;
  const dimensions = [
    ["skills_match", "Skills Match"],
    ["experience_relevance", "Experience"],
    ["education_match", "Education"],
    ["industry_relevance", "Industry"],
    ["keywords", "Keywords"],
    ["stability", "Stability"],
    ["certifications", "Certifications"],
  ];

  const runScreen = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/candidates/${id}/screen`);
      toast.success(`AI compatibility scored: ${r.data.overall}`);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Screening failed");
    } finally {
      setBusy(false);
    }
  };

  const submitSchedule = async () => {
    if (!scheduleAt) return toast.error("Pick a date & time");
    const link = (meetingLink || "").trim();
    if (scheduleRound !== "round1") {
      if (!link) return toast.error("Meeting link is required");
      if (!/^https?:\/\//i.test(link)) return toast.error("Meeting link must start with http:// or https://");
    } else if (link && !/^https?:\/\//i.test(link)) {
      return toast.error("If provided, the link must start with http:// or https://");
    }
    setBusy(true);
    try {
      const r = await api.post(`/candidates/${id}/schedule`, {
        round: scheduleRound,
        scheduled_at: new Date(scheduleAt).toISOString(),
        panel_emails: panel.split(",").map((s) => s.trim()).filter(Boolean),
        meeting_link: link,
        notes: scheduleNotes,
      });
      const verb = isReschedule ? "rescheduled" : "scheduled";
      if (r.data?.email_sent) {
        toast.success(`Interview ${verb} · ${r.data.email_message || "invite emailed"}`);
      } else {
        toast.warning(
          `Interview ${verb}, but email NOT sent: ${r.data?.email_message || "SMTP not configured. Configure it under Email Setup."}`,
          { duration: 8000 }
        );
      }
      setScheduleOpen(false);
      setIsReschedule(false);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Schedule failed");
    } finally {
      setBusy(false);
    }
  };

  const openSchedule = (round, existing = null) => {
    setScheduleRound(round);
    setIsReschedule(!!existing);
    if (existing) {
      // Pre-fill with existing values for reschedule
      const dt = new Date(existing.scheduled_at);
      // datetime-local format: YYYY-MM-DDTHH:mm (local)
      const pad = (n) => String(n).padStart(2, "0");
      setScheduleAt(`${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`);
      setMeetingLink(existing.meeting_link || "");
      setPanel((existing.panel_emails || []).join(", "));
      setScheduleNotes(existing.notes || "");
    } else {
      setScheduleAt("");
      setMeetingLink("");
      setPanel("");
      setScheduleNotes("");
    }
    setScheduleOpen(true);
  };

  const sendFeedbackReminder = async (round) => {
    const label = round === "round1" ? "Round 1" : round === "round2" ? "Round 2" : "Final Round";
    setBusy(true);
    try {
      const r = await api.post(`/candidates/${id}/feedback-reminder?round=${round}`);
      if (r.data?.success) {
        toast.success(`Reminder sent for ${label} → ${(r.data.recipients || []).join(", ")}`);
      } else {
        toast.error(`Failed to send reminder: ${r.data?.message || "SMTP error"}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send reminder");
    } finally {
      setBusy(false);
    }
  };


  const cancelSchedule = async (round) => {
    const label = round === "round1" ? "Round 1 (HR Screening)" : round === "round2" ? "Round 2" : "Final Round";
    if (!window.confirm(`Cancel the scheduled ${label} interview? The candidate will be notified by email.`)) return;
    setBusy(true);
    try {
      const r = await api.delete(`/candidates/${id}/schedule/${round}`);
      toast.success(`${label} cancelled · status reverted to ${r.data.reverted_status.replace(/_/g, " ")}`);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Cancel failed");
    } finally {
      setBusy(false);
    }
  };

  const submitR1 = async () => {
    if (!r1.comments) return toast.error("Comments are mandatory");
    if (r1.career_gap === "yes" && !r1.career_gap_reason.trim()) {
      return toast.error("Career gap reason is required when 'Career gap' is Yes");
    }
    const payload = {
      communication: r1.communication,
      role_fit: r1.role_fit,
      stability: r1.stability,
      strengths: r1.strengths,
      concerns: r1.concerns,
      recommendation: r1.recommendation,
      comments: r1.comments,
      ctc_current_monthly: r1.ctc_current_monthly === "" ? null : Number(r1.ctc_current_monthly),
      ctc_expected_monthly: r1.ctc_expected_monthly === "" ? null : Number(r1.ctc_expected_monthly),
      notice_period: r1.notice_period || null,
      marital_status: r1.marital_status || null,
      current_location: r1.current_location,
      hometown: r1.hometown,
      relocation: r1.relocation || null,
      career_gap: r1.career_gap === "" ? null : r1.career_gap === "yes",
      career_gap_reason: r1.career_gap_reason,
      asset_allocation: r1.asset_allocation === "" ? null : r1.asset_allocation === "yes",
      job_change_reason: r1.job_change_reason,
    };
    setBusy(true);
    try {
      await api.post(`/candidates/${id}/round1-feedback`, payload);
      toast.success("HR screening feedback recorded");
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const submitR2 = async () => {
    if (!r2.comments) return toast.error("Comments are mandatory");
    setBusy(true);
    try {
      await api.post(`/candidates/${id}/round2-feedback`, r2);
      toast.success("Feedback recorded");
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const submitFF = async () => {
    if (!ff.comments) return toast.error("Comments are mandatory");
    setBusy(true);
    try {
      await api.post(`/candidates/${id}/final-feedback`, ff);
      toast.success("Panel feedback recorded");
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const submitDecision = async (action, round) => {
    if (decisionInFlight.current) return;  // guard against double-click
    decisionInFlight.current = true;
    setBusy(true);
    try {
      await api.post(`/candidates/${id}/decision`, { action, comment: decisionComment, round });
      toast.success("Decision recorded · candidate notified");
      setDecisionComment("");
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
      decisionInFlight.current = false;
    }
  };

  const submitOffer = async () => {
    setBusy(true);
    try {
      await api.post(`/candidates/${id}/offer`, {
        offered_ctc: Number(offer.offered_ctc),
        joining_date: offer.joining_date,
        documents_checklist: offer.documents_checklist.split(",").map((s) => s.trim()).filter(Boolean),
        notes: offer.notes,
      });
      toast.success("Offer details saved");
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const deleteCandidate = async () => {
    if (!window.confirm(`Permanently delete ${c.full_name}? This removes their profile, resume, AI score, feedback, schedules, and offer. This cannot be undone.`)) return;
    setBusy(true);
    try {
      await api.delete(`/candidates/${id}`);
      toast.success(`${c.full_name} deleted`);
      // Navigate back to pipeline
      window.location.href = "/candidates";
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
      setBusy(false);
    }
  };

  const isHR = user?.role === "hr";
  const isMgr = user?.role === "manager";

  return (
    <div className="p-8 max-w-[1400px] mx-auto" data-testid="candidate-detail-page">
      <div className="flex items-center justify-between">
        <Link to="/candidates" className="text-xs text-quiet hover:text-ink font-mono">← Pipeline</Link>
        {user?.is_super_admin && (
          <button
            data-testid="delete-candidate-btn"
            onClick={deleteCandidate}
            disabled={busy}
            className="text-xs font-mono uppercase tracking-wider text-[#EF4444] hover:bg-[#FEF2F2] border border-[#EF4444] px-3 py-1.5"
          >
            Delete candidate
          </button>
        )}
      </div>

      <div className="mt-4 border-b border-rule pb-6 flex items-start justify-between gap-6">
        <div className="min-w-0">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-quiet">
            {c.requisition_code || "—"} · {c.requisition_title}
          </div>
          <h1 className="font-serif text-5xl text-ink mt-2 leading-none">{c.full_name}</h1>
          <div className="mt-3 flex flex-wrap gap-2 items-center text-xs">
            <StatusBadge status={c.status} testId="candidate-status-badge" />
            <span className="px-2 py-1 border border-rule font-mono text-quiet bg-white uppercase tracking-wider">
              {c.email}
            </span>
            <span className="px-2 py-1 border border-rule font-mono text-quiet bg-white uppercase tracking-wider">
              {c.mobile}
            </span>
            {c.cooling_until && (
              <span className="px-2 py-1 border border-error text-error font-mono uppercase tracking-wider">
                Cooling until {c.cooling_until.slice(0, 10)}
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          {!score && (
            <button
              data-testid="run-screening-btn"
              onClick={runScreen}
              disabled={busy}
              className="bg-klein hover:bg-klein-hover text-white px-5 py-2.5 text-sm font-medium disabled:opacity-50"
            >
              {busy ? "Scoring…" : "Run AI Screening →"}
            </button>
          )}
          {isHR && score && c.status === "screened" && (
            <button
              data-testid="schedule-r1-btn"
              onClick={() => openSchedule("round1")}
              className="bg-klein hover:bg-klein-hover text-white px-5 py-2.5 text-sm font-medium"
            >
              Schedule Round 1 (HR) →
            </button>
          )}
          {isHR && c.status === "round1_feedback" && (
            <button
              data-testid="schedule-r2-btn"
              onClick={() => openSchedule("round2")}
              className="bg-klein hover:bg-klein-hover text-white px-5 py-2.5 text-sm font-medium"
            >
              Schedule Round 2 →
            </button>
          )}
          {isHR && (c.status === "round2_feedback") && (
            <button
              data-testid="schedule-final-btn"
              onClick={() => openSchedule("final")}
              className="bg-klein hover:bg-klein-hover text-white px-5 py-2.5 text-sm font-medium"
            >
              Schedule Final →
            </button>
          )}
        </div>
      </div>

      <div className="mt-6 flex gap-1 border-b border-rule">
        {[
          ["overview", "Overview"],
          ["screening", "AI Screening"],
          ["round1", "Round 1 (HR)"],
          ["round2", "Round 2"],
          ["final", "Final Round"],
          ["offer", "Offer"],
        ].map(([k, l]) => (
          <button
            key={k}
            data-testid={`tab-${k}`}
            onClick={() => setTab(k)}
            className={`px-4 py-2 text-sm font-mono uppercase tracking-wider border-b-2 -mb-px ${
              tab === k ? "border-klein text-ink" : "border-transparent text-quiet hover:text-ink"
            }`}
          >
            {l}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <section className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6 fade-up">
          <div className="lg:col-span-2 bg-white border border-rule p-6 space-y-5">
            <h3 className="font-serif text-2xl text-ink">Profile</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <Detail label="Total exp" value={`${c.total_experience} yrs`} />
              <Detail label="Relevant exp" value={`${c.relevant_experience} yrs`} />
              <Detail label="Notice period" value={c.notice_period || "—"} />
              <Detail label="Current company" value={c.current_company || "—"} />
              <Detail label="Current CTC" value={c.current_ctc ? `${c.current_ctc} LPA` : "—"} />
              <Detail label="Expected CTC" value={c.expected_ctc ? `${c.expected_ctc} LPA` : "—"} />
              <Detail label="Location" value={c.current_location} />
              <Detail label="Qualification" value={c.highest_qualification} />
              <Detail label="Portfolio" value={c.portfolio_url ? (<a href={c.portfolio_url} className="text-klein hover:underline" target="_blank" rel="noreferrer">Open ↗</a>) : "—"} />
            </div>
            <div className="pt-4 border-t border-rule">
              <div className="font-mono text-[10px] uppercase tracking-wider text-quiet">Declared skills</div>
              <div className="mt-2 flex flex-wrap gap-2">
                {(c.skills || "").split(",").map((s) => s.trim()).filter(Boolean).map((s) => (
                  <span key={s} className="px-2 py-1 bg-paper border border-rule text-xs font-mono">{s}</span>
                ))}
              </div>
            </div>
            {(c.resume_file_b64 || c.resume_text) && (
              <div className="pt-4 border-t border-rule" data-testid="resume-section">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-mono text-[10px] uppercase tracking-wider text-quiet">
                    Resume{c.resume_filename ? ` · ${c.resume_filename}` : ""}
                  </div>
                  {resumeUrl && (
                    <div className="flex gap-2">
                      <a
                        data-testid="open-resume-btn"
                        href={resumeUrl.url}
                        target="_blank"
                        rel="noreferrer"
                        className="border border-ink hover:bg-paper text-ink px-3 py-1.5 text-xs font-mono uppercase tracking-wider"
                      >
                        Open ↗
                      </a>
                      <button
                        data-testid="download-resume-btn"
                        onClick={downloadResume}
                        className="bg-ink hover:bg-black text-white px-3 py-1.5 text-xs font-mono uppercase tracking-wider"
                      >
                        Download ↓
                      </button>
                    </div>
                  )}
                </div>
                {resumeUrl && resumeUrl.ext === "pdf" ? (
                  <iframe
                    data-testid="resume-iframe"
                    src={resumeUrl.url}
                    title="Resume"
                    className="w-full border border-rule bg-paper"
                    style={{ height: 640 }}
                  />
                ) : resumeUrl ? (
                  <div className="bg-paper border border-rule p-4 text-xs text-quiet">
                    Inline preview is only available for PDF files. Use the Download button above to view this resume.
                  </div>
                ) : null}
                {c.resume_text && (
                  <details className="mt-3" data-testid="resume-text-toggle">
                    <summary className="cursor-pointer font-mono text-[10px] uppercase tracking-wider text-quiet hover:text-ink">
                      Show extracted text
                    </summary>
                    <pre className="whitespace-pre-wrap text-xs text-ink bg-paper border border-rule p-3 max-h-80 overflow-auto font-sans leading-relaxed mt-2">{c.resume_text}</pre>
                  </details>
                )}
              </div>
            )}
          </div>

          <aside className="space-y-4">
            {score ? (
              <div className="bg-white border border-rule p-5 relative overflow-hidden">
                <div
                  className="absolute inset-0 opacity-30 pointer-events-none"
                  style={{ backgroundImage: "url(https://images.unsplash.com/photo-1765046255479-669cf07a0230)", backgroundSize: "cover", backgroundPosition: "center" }}
                />
                <div className="relative">
                  <ScoreRadial score={score.overall || 0} />
                  <div className="mt-5 space-y-2">
                    {dimensions.map(([k, l]) => (
                      <ScoreBar key={k} label={l} value={score[k] || 0} />
                    ))}
                  </div>
                  {score.strengths && (
                    <div className="mt-4 pt-4 border-t border-rule">
                      <div className="font-mono text-[10px] uppercase tracking-wider text-quiet">Strengths</div>
                      <div className="text-xs text-ink mt-1">{score.strengths}</div>
                    </div>
                  )}
                  {score.concerns && (
                    <div className="mt-3">
                      <div className="font-mono text-[10px] uppercase tracking-wider text-quiet">Concerns</div>
                      <div className="text-xs text-ink mt-1">{score.concerns}</div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-white border border-rule p-5 text-center">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-quiet">No AI score yet</div>
                <p className="text-xs text-quiet mt-2">Run AI screening to compute compatibility against the JD.</p>
              </div>
            )}
          </aside>
        </section>
      )}

      {tab === "screening" && (
        <section className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6 fade-up">
          <div className="bg-white border border-rule p-5">
            <h3 className="font-serif text-2xl text-ink mb-4">Compatibility Score</h3>
            {score ? (
              <>
                <ScoreRadial score={score.overall || 0} size={180} />
                <div className="mt-5 space-y-2">
                  {dimensions.map(([k, l]) => (
                    <ScoreBar key={k} label={l} value={score[k] || 0} />
                  ))}
                </div>
              </>
            ) : (
              <button
                data-testid="run-screening-btn-tab"
                onClick={runScreen}
                disabled={busy}
                className="w-full bg-klein hover:bg-klein-hover text-white px-5 py-3 text-sm font-medium"
              >
                {busy ? "Scoring with Replicate…" : "Run AI Screening →"}
              </button>
            )}
          </div>
          {score && (
            <div className="bg-white border border-rule p-5 space-y-4">
              <h3 className="font-serif text-2xl text-ink">Insights</h3>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-wider text-quiet">Strengths</div>
                <div className="text-sm text-ink mt-1">{score.strengths || "—"}</div>
              </div>
              <div>
                <div className="font-mono text-[10px] uppercase tracking-wider text-quiet">Concerns</div>
                <div className="text-sm text-ink mt-1">{score.concerns || "—"}</div>
              </div>
              <div className="flex flex-wrap gap-2 pt-2">
                <button
                  data-testid="rerun-screening-btn"
                  onClick={runScreen}
                  disabled={busy}
                  className="border border-ink hover:bg-paper px-4 py-2 text-sm"
                >
                  Re-run screening
                </button>
                {isHR && c.status === "screened" && (
                  <button
                    data-testid="schedule-r1-from-ai-btn"
                    onClick={() => openSchedule("round1")}
                    className="bg-klein hover:bg-klein-hover text-white px-4 py-2 text-sm font-medium"
                  >
                    Schedule Round 1 (HR) →
                  </button>
                )}
              </div>
            </div>
          )}
        </section>
      )}

      {tab === "round1" && (
        <section className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6 fade-up" data-testid="r1-section">
          <div className="bg-white border border-rule p-5">
            <div className="flex items-start justify-between gap-3">
              <h3 className="font-serif text-2xl text-ink">Round 1 schedule</h3>
              {(isHR || isMgr) && c.schedule_round1 && !c.round1_feedback && (
                <div className="flex gap-2 flex-wrap">
                  <button
                    data-testid="remind-r1-btn"
                    onClick={() => sendFeedbackReminder("round1")}
                    disabled={busy}
                    className="border border-ink hover:bg-paper px-3 py-1.5 text-xs font-mono uppercase tracking-wider"
                  >
                    Remind for feedback
                  </button>
                  {isHR && (
                    <>
                      <button
                        data-testid="reschedule-r1-btn"
                        onClick={() => openSchedule("round1", c.schedule_round1)}
                        className="border border-ink hover:bg-paper px-3 py-1.5 text-xs font-mono uppercase tracking-wider"
                      >
                        Reschedule
                      </button>
                      <button
                        data-testid="cancel-r1-btn"
                        onClick={() => cancelSchedule("round1")}
                        disabled={busy}
                        className="border border-[#EF4444] text-[#EF4444] hover:bg-[#FEF2F2] px-3 py-1.5 text-xs font-mono uppercase tracking-wider"
                      >
                        Cancel
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
            {c.schedule_round1 ? (
              <div className="mt-4 space-y-2 text-sm">
                <Detail label="Scheduled at" value={new Date(c.schedule_round1.scheduled_at).toLocaleString()} />
                <Detail label="Mode / link" value={c.schedule_round1.meeting_link || "Phone / async — no link"} />
                <Detail label="Panel" value={(c.schedule_round1.panel_emails || []).join(", ") || "—"} />
                <Detail label="Notes" value={c.schedule_round1.notes || "—"} />
              </div>
            ) : (
              <p className="text-quiet text-sm mt-3">Not scheduled.</p>
            )}
          </div>

          <div className="bg-white border border-rule p-5">
            <h3 className="font-serif text-2xl text-ink">HR screening feedback</h3>
            {c.round1_feedback ? (
              <div className="mt-4 space-y-2 text-sm" data-testid="r1-feedback-view">
                <Detail label="Communication" value={`${c.round1_feedback.communication}/5`} />
                <Detail label="Role fit" value={`${c.round1_feedback.role_fit}/5`} />
                <Detail label="Stability" value={`${c.round1_feedback.stability}/5`} />
                <Detail label="Recommendation" value={c.round1_feedback.recommendation} />
                <Detail label="Strengths" value={c.round1_feedback.strengths} />
                <Detail label="Concerns" value={c.round1_feedback.concerns} />
                <Detail label="Comments" value={c.round1_feedback.comments} />
                <div className="pt-3 mt-3 border-t border-rule">
                  <div className="font-mono text-[10px] uppercase tracking-wider text-quiet mb-2">HR data captured</div>
                  <Detail label="CTC current (monthly)" value={c.round1_feedback.ctc_current_monthly ?? "—"} />
                  <Detail label="CTC expected (monthly)" value={c.round1_feedback.ctc_expected_monthly ?? "—"} />
                  <Detail label="Notice period" value={(c.round1_feedback.notice_period || "—").replace(/_/g, " ")} />
                  <Detail label="Marital status" value={c.round1_feedback.marital_status || "—"} />
                  <Detail label="Current location" value={c.round1_feedback.current_location || "—"} />
                  <Detail label="Hometown" value={c.round1_feedback.hometown || "—"} />
                  <Detail label="Relocation" value={(c.round1_feedback.relocation || "—").replace(/_/g, " ")} />
                  <Detail label="Career gap" value={c.round1_feedback.career_gap === null ? "—" : c.round1_feedback.career_gap ? "Yes" : "No"} />
                  {c.round1_feedback.career_gap && (
                    <Detail label="Career gap reason" value={c.round1_feedback.career_gap_reason || "—"} />
                  )}
                  <Detail label="Asset allocation" value={c.round1_feedback.asset_allocation === null ? "—" : c.round1_feedback.asset_allocation ? "Yes" : "No"} />
                  <Detail label="Job change reason" value={c.round1_feedback.job_change_reason || "—"} />
                </div>
              </div>
            ) : (isHR || isMgr) && c.status === "round1_scheduled" ? (
              <div className="mt-4 space-y-3" data-testid="r1-feedback-form">
                <Slider label="Communication" value={r1.communication} onChange={(v) => setR1({ ...r1, communication: v })} />
                <Slider label="Role fit" value={r1.role_fit} onChange={(v) => setR1({ ...r1, role_fit: v })} />
                <Slider label="Stability" value={r1.stability} onChange={(v) => setR1({ ...r1, stability: v })} />

                <div className="pt-3 mt-3 border-t border-rule">
                  <div className="font-mono text-[10px] uppercase tracking-wider text-quiet mb-2">HR data capture</div>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="CTC current (monthly)">
                      <input data-testid="r1-ctc-current" type="number" min={0} value={r1.ctc_current_monthly} onChange={(e) => setR1({ ...r1, ctc_current_monthly: e.target.value })} className={inp} />
                    </Field>
                    <Field label="CTC expected (monthly)">
                      <input data-testid="r1-ctc-expected" type="number" min={0} value={r1.ctc_expected_monthly} onChange={(e) => setR1({ ...r1, ctc_expected_monthly: e.target.value })} className={inp} />
                    </Field>
                    <Field label="Notice period">
                      <select data-testid="r1-notice" value={r1.notice_period} onChange={(e) => setR1({ ...r1, notice_period: e.target.value })} className={inp}>
                        <option value="">—</option>
                        <option value="90_days">90 days</option>
                        <option value="60_days">60 days</option>
                        <option value="45_days">45 days</option>
                        <option value="30_days">30 days</option>
                        <option value="negotiable">Negotiable</option>
                        <option value="serving">Serving notice</option>
                        <option value="not_required">Not required</option>
                      </select>
                    </Field>
                    <Field label="Marital status">
                      <select data-testid="r1-marital" value={r1.marital_status} onChange={(e) => setR1({ ...r1, marital_status: e.target.value })} className={inp}>
                        <option value="">—</option>
                        <option value="married">Married</option>
                        <option value="unmarried">Unmarried</option>
                      </select>
                    </Field>
                    <Field label="Current location">
                      <input data-testid="r1-location" value={r1.current_location} onChange={(e) => setR1({ ...r1, current_location: e.target.value })} className={inp} />
                    </Field>
                    <Field label="Hometown">
                      <input data-testid="r1-hometown" value={r1.hometown} onChange={(e) => setR1({ ...r1, hometown: e.target.value })} className={inp} />
                    </Field>
                    <Field label="Relocation">
                      <select data-testid="r1-relocation" value={r1.relocation} onChange={(e) => setR1({ ...r1, relocation: e.target.value })} className={inp}>
                        <option value="">—</option>
                        <option value="open">Open</option>
                        <option value="not_open">Not open</option>
                        <option value="not_required">Not required</option>
                      </select>
                    </Field>
                    <Field label="Career gap?">
                      <select data-testid="r1-gap" value={r1.career_gap} onChange={(e) => setR1({ ...r1, career_gap: e.target.value })} className={inp}>
                        <option value="">—</option>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                      </select>
                    </Field>
                  </div>
                  {r1.career_gap === "yes" && (
                    <div className="mt-3">
                      <Field label="Career gap reason *">
                        <textarea data-testid="r1-gap-reason" rows={2} value={r1.career_gap_reason} onChange={(e) => setR1({ ...r1, career_gap_reason: e.target.value })} className={inp} />
                      </Field>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-3 mt-3">
                    <Field label="Asset allocation (laptop/IT)?">
                      <select data-testid="r1-asset" value={r1.asset_allocation} onChange={(e) => setR1({ ...r1, asset_allocation: e.target.value })} className={inp}>
                        <option value="">—</option>
                        <option value="yes">Yes — required</option>
                        <option value="no">No</option>
                      </select>
                    </Field>
                  </div>
                  <div className="mt-3">
                    <Field label="Reason for job change">
                      <textarea data-testid="r1-job-reason" rows={2} value={r1.job_change_reason} onChange={(e) => setR1({ ...r1, job_change_reason: e.target.value })} className={inp} />
                    </Field>
                  </div>
                </div>

                <div className="pt-3 mt-3 border-t border-rule">
                  <div className="font-mono text-[10px] uppercase tracking-wider text-quiet mb-2">Screening summary</div>
                  <Field label="Strengths">
                    <textarea data-testid="r1-strengths" rows={2} value={r1.strengths} onChange={(e) => setR1({ ...r1, strengths: e.target.value })} className={inp} />
                  </Field>
                  <Field label="Concerns">
                    <textarea data-testid="r1-concerns" rows={2} value={r1.concerns} onChange={(e) => setR1({ ...r1, concerns: e.target.value })} className={inp} />
                  </Field>
                  <Field label="Recommendation">
                    <div className="grid grid-cols-3 gap-2">
                      {["reject", "hold", "proceed"].map((r) => (
                        <button
                          key={r}
                          type="button"
                          data-testid={`r1-rec-${r}`}
                          onClick={() => setR1({ ...r1, recommendation: r })}
                          className={`px-3 py-2 border font-mono text-xs uppercase ${
                            r1.recommendation === r ? "bg-ink text-white border-ink" : "border-rule text-quiet hover:border-ink"
                          }`}
                        >
                          {r}
                        </button>
                      ))}
                    </div>
                  </Field>
                  <Field label="Comments (mandatory)">
                    <textarea data-testid="r1-comments" rows={3} value={r1.comments} onChange={(e) => setR1({ ...r1, comments: e.target.value })} className={inp} />
                  </Field>
                </div>

                <button data-testid="submit-r1-feedback" onClick={submitR1} disabled={busy} className="bg-klein hover:bg-klein-hover text-white px-5 py-2.5 text-sm">
                  Submit feedback
                </button>
              </div>
            ) : (
              <p className="text-quiet text-sm mt-3">
                {c.status === "round1_scheduled" ? "Awaiting HR feedback." : "Not yet at this stage."}
              </p>
            )}

            {isHR && c.status === "round1_feedback" && (
              <div className="mt-5 pt-4 border-t border-rule">
                <div className="font-mono text-[10px] uppercase tracking-wider text-quiet mb-2">HR decision</div>
                <textarea
                  data-testid="r1-decision-comment"
                  rows={2}
                  value={decisionComment}
                  onChange={(e) => setDecisionComment(e.target.value)}
                  placeholder="Reason / comment"
                  className={inp}
                />
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <button data-testid="r1-reject-btn" onClick={() => submitDecision("reject", "round1")} disabled={busy} className="border border-error text-error hover:bg-[#FEF2F2] px-3 py-2 text-sm">
                    Reject (notify candidate)
                  </button>
                  <button data-testid="r1-proceed-btn" onClick={() => submitDecision("proceed", "round1")} disabled={busy} className="bg-klein hover:bg-klein-hover text-white px-3 py-2 text-sm">
                    Proceed to Round 2 →
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {tab === "round2" && (
        <section className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6 fade-up">
          <div className="bg-white border border-rule p-5">
            <div className="flex items-start justify-between gap-3">
              <h3 className="font-serif text-2xl text-ink">Round 2 schedule</h3>
              {(isHR || isMgr) && c.schedule_round2 && !c.round2_feedback && (
                <div className="flex gap-2 flex-wrap">
                  <button
                    data-testid="remind-r2-btn"
                    onClick={() => sendFeedbackReminder("round2")}
                    disabled={busy}
                    className="border border-ink hover:bg-paper px-3 py-1.5 text-xs font-mono uppercase tracking-wider"
                  >
                    Remind for feedback
                  </button>
                  {isHR && (
                    <>
                      <button
                        data-testid="reschedule-r2-btn"
                        onClick={() => openSchedule("round2", c.schedule_round2)}
                        className="border border-ink hover:bg-paper px-3 py-1.5 text-xs font-mono uppercase tracking-wider"
                      >
                        Reschedule
                      </button>
                      <button
                        data-testid="cancel-r2-btn"
                        onClick={() => cancelSchedule("round2")}
                        disabled={busy}
                        className="border border-[#EF4444] text-[#EF4444] hover:bg-[#FEF2F2] px-3 py-1.5 text-xs font-mono uppercase tracking-wider"
                      >
                        Cancel
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
            {c.schedule_round2 ? (
              <div className="mt-4 space-y-2 text-sm">
                <Detail label="Scheduled at" value={new Date(c.schedule_round2.scheduled_at).toLocaleString()} />
                <Detail label="Meeting link" value={c.schedule_round2.meeting_link || "—"} />
                <Detail label="Panel" value={(c.schedule_round2.panel_emails || []).join(", ") || "—"} />
              </div>
            ) : (
              <p className="text-quiet text-sm mt-3">Not scheduled.</p>
            )}
          </div>

          <div className="bg-white border border-rule p-5">
            <h3 className="font-serif text-2xl text-ink">Manager feedback</h3>
            {c.round2_feedback ? (
              <div className="mt-4 space-y-2 text-sm">
                <Detail label="Technical fit" value={`${c.round2_feedback.technical_fit}/5`} />
                <Detail label="Communication" value={`${c.round2_feedback.communication}/5`} />
                <Detail label="Culture fit" value={`${c.round2_feedback.culture_fit}/5`} />
                <Detail label="Recommendation" value={c.round2_feedback.recommendation} />
                <Detail label="Strengths" value={c.round2_feedback.strengths} />
                <Detail label="Concerns" value={c.round2_feedback.concerns} />
                <Detail label="Comments" value={c.round2_feedback.comments} />
              </div>
            ) : isMgr && c.status === "round2_scheduled" ? (
              <div className="mt-4 space-y-3">
                <Slider label="Technical fit" value={r2.technical_fit} onChange={(v) => setR2({ ...r2, technical_fit: v })} />
                <Slider label="Communication" value={r2.communication} onChange={(v) => setR2({ ...r2, communication: v })} />
                <Slider label="Culture fit" value={r2.culture_fit} onChange={(v) => setR2({ ...r2, culture_fit: v })} />
                <Field label="Strengths">
                  <textarea data-testid="r2-strengths" rows={2} value={r2.strengths} onChange={(e) => setR2({ ...r2, strengths: e.target.value })} className={inp} />
                </Field>
                <Field label="Concerns">
                  <textarea data-testid="r2-concerns" rows={2} value={r2.concerns} onChange={(e) => setR2({ ...r2, concerns: e.target.value })} className={inp} />
                </Field>
                <Field label="Recommendation">
                  <div className="grid grid-cols-3 gap-2">
                    {["reject", "hold", "proceed"].map((r) => (
                      <button
                        key={r}
                        type="button"
                        data-testid={`r2-rec-${r}`}
                        onClick={() => setR2({ ...r2, recommendation: r })}
                        className={`px-3 py-2 border font-mono text-xs uppercase ${
                          r2.recommendation === r ? "bg-ink text-white border-ink" : "border-rule text-quiet hover:border-ink"
                        }`}
                      >
                        {r}
                      </button>
                    ))}
                  </div>
                </Field>
                <Field label="Comments (mandatory)">
                  <textarea data-testid="r2-comments" rows={3} value={r2.comments} onChange={(e) => setR2({ ...r2, comments: e.target.value })} className={inp} />
                </Field>
                <button data-testid="submit-r2-feedback" onClick={submitR2} disabled={busy} className="bg-klein hover:bg-klein-hover text-white px-5 py-2.5 text-sm">
                  Submit feedback
                </button>
              </div>
            ) : (
              <p className="text-quiet text-sm mt-3">
                {c.status === "round2_scheduled" ? "Awaiting manager feedback." : "Not yet at this stage."}
              </p>
            )}

            {isHR && c.status === "round2_feedback" && (
              <div className="mt-5 pt-4 border-t border-rule">
                <div className="font-mono text-[10px] uppercase tracking-wider text-quiet mb-2">HR decision</div>
                <textarea
                  data-testid="r2-decision-comment"
                  rows={2}
                  value={decisionComment}
                  onChange={(e) => setDecisionComment(e.target.value)}
                  placeholder="Reason / comment"
                  className={inp}
                />
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <button data-testid="r2-reject-btn" onClick={() => submitDecision("reject", "round2")} disabled={busy} className="border border-error text-error hover:bg-[#FEF2F2] px-3 py-2 text-sm">
                    Reject (notify candidate)
                  </button>
                  <button data-testid="r2-proceed-btn" onClick={() => submitDecision("proceed", "round2")} disabled={busy} className="bg-klein hover:bg-klein-hover text-white px-3 py-2 text-sm">
                    Proceed to Final →
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {tab === "final" && (
        <section className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6 fade-up">
          <div className="bg-white border border-rule p-5">
            <div className="flex items-start justify-between gap-3">
              <h3 className="font-serif text-2xl text-ink">Final round schedule</h3>
              {(isHR || isMgr) && c.schedule_final && !c.final_feedback && (
                <div className="flex gap-2 flex-wrap">
                  <button
                    data-testid="remind-final-btn"
                    onClick={() => sendFeedbackReminder("final")}
                    disabled={busy}
                    className="border border-ink hover:bg-paper px-3 py-1.5 text-xs font-mono uppercase tracking-wider"
                  >
                    Remind for feedback
                  </button>
                  {isHR && (
                    <>
                      <button
                        data-testid="reschedule-final-btn"
                        onClick={() => openSchedule("final", c.schedule_final)}
                        className="border border-ink hover:bg-paper px-3 py-1.5 text-xs font-mono uppercase tracking-wider"
                      >
                        Reschedule
                      </button>
                      <button
                        data-testid="cancel-final-btn"
                        onClick={() => cancelSchedule("final")}
                        disabled={busy}
                        className="border border-[#EF4444] text-[#EF4444] hover:bg-[#FEF2F2] px-3 py-1.5 text-xs font-mono uppercase tracking-wider"
                      >
                        Cancel
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
            {c.schedule_final ? (
              <div className="mt-4 space-y-2 text-sm">
                <Detail label="Scheduled at" value={new Date(c.schedule_final.scheduled_at).toLocaleString()} />
                <Detail label="Meeting link" value={c.schedule_final.meeting_link || "—"} />
                <Detail label="Panel" value={(c.schedule_final.panel_emails || []).join(", ") || "—"} />
              </div>
            ) : (
              <p className="text-quiet text-sm mt-3">Not scheduled.</p>
            )}
          </div>
          <div className="bg-white border border-rule p-5">
            <h3 className="font-serif text-2xl text-ink">Panel feedback</h3>
            {c.final_feedback ? (
              <div className="mt-4 space-y-2 text-sm">
                <Detail label="Technical depth" value={`${c.final_feedback.technical_depth}/5`} />
                <Detail label="Leadership" value={`${c.final_feedback.leadership}/5`} />
                <Detail label="Behavioral fit" value={`${c.final_feedback.behavioral_fit}/5`} />
                <Detail label="Salary fit" value={`${c.final_feedback.salary_fit}/5`} />
                <Detail label="Overall" value={c.final_feedback.overall} />
                <Detail label="Comments" value={c.final_feedback.comments} />
              </div>
            ) : c.status === "final_scheduled" ? (
              <div className="mt-4 space-y-3">
                <Slider label="Technical depth" value={ff.technical_depth} onChange={(v) => setFf({ ...ff, technical_depth: v })} />
                <Slider label="Leadership" value={ff.leadership} onChange={(v) => setFf({ ...ff, leadership: v })} />
                <Slider label="Behavioral fit" value={ff.behavioral_fit} onChange={(v) => setFf({ ...ff, behavioral_fit: v })} />
                <Slider label="Salary fit" value={ff.salary_fit} onChange={(v) => setFf({ ...ff, salary_fit: v })} />
                <Field label="Overall recommendation">
                  <div className="grid grid-cols-3 gap-2">
                    {["reject", "hold", "select"].map((r) => (
                      <button
                        key={r}
                        type="button"
                        data-testid={`ff-overall-${r}`}
                        onClick={() => setFf({ ...ff, overall: r })}
                        className={`px-3 py-2 border font-mono text-xs uppercase ${
                          ff.overall === r ? "bg-ink text-white border-ink" : "border-rule text-quiet hover:border-ink"
                        }`}
                      >
                        {r}
                      </button>
                    ))}
                  </div>
                </Field>
                <Field label="Comments (mandatory)">
                  <textarea data-testid="ff-comments" rows={3} value={ff.comments} onChange={(e) => setFf({ ...ff, comments: e.target.value })} className={inp} />
                </Field>
                <button data-testid="submit-ff-feedback" onClick={submitFF} disabled={busy} className="bg-klein hover:bg-klein-hover text-white px-5 py-2.5 text-sm">
                  Submit feedback
                </button>
              </div>
            ) : (
              <p className="text-quiet text-sm mt-3">Not yet at this stage.</p>
            )}

            {isHR && c.status === "final_feedback" && (
              <div className="mt-5 pt-4 border-t border-rule">
                <div className="font-mono text-[10px] uppercase tracking-wider text-quiet mb-2">HR final decision</div>
                <textarea data-testid="ff-decision-comment" rows={2} value={decisionComment} onChange={(e) => setDecisionComment(e.target.value)} placeholder="Reason / note" className={inp} />
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <button data-testid="ff-reject-btn" onClick={() => submitDecision("reject", "final")} disabled={busy} className="border border-error text-error hover:bg-[#FEF2F2] px-3 py-2 text-sm">
                    Reject
                  </button>
                  <button data-testid="ff-select-btn" onClick={() => submitDecision("select", "final")} disabled={busy} className="bg-klein hover:bg-klein-hover text-white px-3 py-2 text-sm">
                    Select →
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {tab === "offer" && (
        <section className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6 fade-up">
          <div className="bg-white border border-rule p-5">
            <h3 className="font-serif text-2xl text-ink mb-2">Offer details</h3>
            {c.status !== "selected" && !c.offer && (
              <p className="text-sm text-quiet">Candidate must be Selected before offer can be drafted.</p>
            )}
            {(isHR && (c.status === "selected" || c.offer)) && (
              <div className="space-y-3">
                <Field label="Offered CTC (LPA)">
                  <input data-testid="offer-ctc" type="number" value={offer.offered_ctc} onChange={(e) => setOffer({ ...offer, offered_ctc: e.target.value })} className={inp} />
                </Field>
                <Field label="Joining date">
                  <input data-testid="offer-date" type="date" value={offer.joining_date} onChange={(e) => setOffer({ ...offer, joining_date: e.target.value })} className={inp} />
                </Field>
                <Field label="Documents checklist (comma separated)">
                  <textarea data-testid="offer-docs" rows={3} value={offer.documents_checklist} onChange={(e) => setOffer({ ...offer, documents_checklist: e.target.value })} className={inp} />
                </Field>
                <Field label="Notes">
                  <textarea data-testid="offer-notes" rows={2} value={offer.notes} onChange={(e) => setOffer({ ...offer, notes: e.target.value })} className={inp} />
                </Field>
                <button data-testid="save-offer-btn" onClick={submitOffer} disabled={busy} className="bg-klein hover:bg-klein-hover text-white px-5 py-2.5 text-sm">
                  Save offer
                </button>
              </div>
            )}
          </div>
          <div className="bg-white border border-rule p-5">
            <h3 className="font-serif text-2xl text-ink mb-2">Pre-joining</h3>
            {c.offer ? (
              <div className="space-y-2 text-sm">
                <Detail label="Offered CTC" value={`${c.offer.offered_ctc} LPA`} />
                <Detail label="Joining date" value={c.offer.joining_date} />
                <div className="pt-3 border-t border-rule">
                  <div className="font-mono text-[10px] uppercase tracking-wider text-quiet">Documents checklist</div>
                  <ul className="mt-2 space-y-1.5">
                    {(c.offer.documents_checklist || []).map((d) => (
                      <li key={d} className="flex items-center gap-2 text-sm text-ink">
                        <span className="w-3 h-3 border border-ink inline-block" />
                        {d}
                      </li>
                    ))}
                  </ul>
                </div>
                {c.offer.notes && <Detail label="Notes" value={c.offer.notes} />}
                {(c.offer.stage || c.status) && (
                  <div className="pt-3 mt-3 border-t border-rule">
                    <div className="font-mono text-[10px] uppercase tracking-wider text-quiet mb-2">Hire stage</div>
                    <StageBar status={c.status} />
                    {c.status === "joined" && (
                      <div className="mt-3 space-y-1 text-sm">
                        <Detail label="Employee ID" value={c.offer.employee_id} />
                        <Detail label="Reporting manager" value={c.offer.reporting_manager} />
                        <Detail label="Actual joining date" value={c.offer.actual_joining_date} />
                      </div>
                    )}
                    {isHR && <HireActions c={c} onDone={reload} />}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-quiet text-sm">No offer drafted yet.</p>
            )}
          </div>
        </section>
      )}

      {scheduleOpen && (
        <div className="fixed inset-0 bg-white/80 backdrop-blur-md flex items-center justify-center z-50 p-4">
          <div className="bg-white border border-ink max-w-md w-full p-6 fade-in">
            <div className="flex items-start justify-between">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-quiet">
                  Schedule · {scheduleRound === "round1" ? "Round 1 (HR Screening)" : scheduleRound === "round2" ? "Round 2" : "Final Round"}
                </div>
                <h3 className="font-serif text-3xl text-ink mt-1">Book interview.</h3>
              </div>
              <button onClick={() => setScheduleOpen(false)} className="text-quiet hover:text-ink text-xl leading-none" data-testid="close-schedule">
                ×
              </button>
            </div>
            <div className="mt-5 space-y-4">
              <Field label="Date & time *">
                <input data-testid="schedule-datetime" type="datetime-local" required value={scheduleAt} onChange={(e) => setScheduleAt(e.target.value)} className={inp} />
              </Field>
              <Field label={scheduleRound === "round1" ? "Meeting link (optional · phone or async OK)" : "Meeting link (Zoom/Meet) *"}>
                <input
                  data-testid="schedule-meeting"
                  type="url"
                  required={scheduleRound !== "round1"}
                  value={meetingLink}
                  onChange={(e) => setMeetingLink(e.target.value)}
                  placeholder={scheduleRound === "round1" ? "Leave blank for phone screens" : "https://meet.google.com/abc-defg-hij"}
                  className={inp}
                />
              </Field>
              <Field label={scheduleRound === "round1" ? "Co-panel emails (optional)" : "Panel emails (comma separated)"}>
                <input data-testid="schedule-panel" value={panel} onChange={(e) => setPanel(e.target.value)} placeholder="vp@company.com, lead@company.com" className={inp} />
              </Field>
              <Field label="Notes for candidate">
                <textarea data-testid="schedule-notes" rows={2} value={scheduleNotes} onChange={(e) => setScheduleNotes(e.target.value)} className={inp} />
              </Field>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button onClick={() => setScheduleOpen(false)} className="border border-ink px-4 py-2 text-sm hover:bg-paper">
                Cancel
              </button>
              <button
                data-testid="confirm-schedule"
                onClick={submitSchedule}
                disabled={busy || !scheduleAt || (scheduleRound !== "round1" && !meetingLink.trim())}
                className="bg-klein hover:bg-klein-hover text-white px-4 py-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {busy ? "Saving…" : (isReschedule ? "Save new time + notify →" : "Schedule + invite →")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const inp =
  "w-full bg-transparent border border-rule px-3 py-2 focus:outline-none focus:border-klein focus:ring-1 focus:ring-klein font-sans text-sm";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-quiet">{label}</span>
    <div className="mt-1.5">{children}</div>
  </label>
);

const Detail = ({ label, value }) => (
  <div>
    <div className="font-mono text-[10px] uppercase tracking-wider text-quiet">{label}</div>
    <div className="text-sm text-ink mt-1">{value}</div>
  </div>
);

const Slider = ({ label, value, onChange }) => (
  <div>
    <div className="flex items-center justify-between font-mono text-[11px] text-quiet">
      <span className="uppercase tracking-wider">{label}</span>
      <span className="text-ink">{value}/5</span>
    </div>
    <input
      type="range"
      min={1}
      max={5}
      step={1}
      value={value}
      onChange={(e) => onChange(Number(e.target.value))}
      className="w-full mt-1.5 accent-[#002FA7]"
    />
  </div>
);


const STAGES = [
  { key: "offer_extended", label: "Offer sent" },
  { key: "offer_accepted", label: "Accepted" },
  { key: "joined",         label: "Joined" },
];

const StageBar = ({ status }) => {
  const idx = STAGES.findIndex((s) => s.key === status);
  const declined = status === "offer_declined";
  const dropped  = status === "dropped";
  return (
    <div className="flex items-center gap-2" data-testid="hire-stagebar">
      {STAGES.map((s, i) => {
        const done = idx >= i && !declined && !dropped;
        const active = idx === i && !declined && !dropped;
        return (
          <div key={s.key} className="flex items-center gap-2 flex-1">
            <div className={`w-3 h-3 rounded-full ${done ? "bg-klein" : "bg-rule"} ${active ? "ring-2 ring-klein/40" : ""}`} />
            <div className={`text-xs ${done ? "text-ink" : "text-quiet"}`}>{s.label}</div>
            {i < STAGES.length - 1 && <div className={`h-px flex-1 ${done ? "bg-klein" : "bg-rule"}`} />}
          </div>
        );
      })}
      {(declined || dropped) && (
        <div className="text-xs font-mono uppercase text-error ml-2" data-testid={`stage-${status}`}>
          {declined ? "· Declined" : "· Dropped"}
        </div>
      )}
    </div>
  );
};

const HireActions = ({ c, onDone }) => {
  const [busy, setBusy] = useState(false);
  const [showJoin, setShowJoin] = useState(false);
  const [join, setJoin] = useState({ actual_joining_date: "", employee_id: "", reporting_manager: "" });

  const call = async (stage, extra = {}, confirmMsg = null) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBusy(true);
    try {
      await api.post(`/candidates/${c.id}/hire-stage`, { stage, ...extra });
      toast.success(`Marked ${stage}`);
      onDone();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const submitJoin = () => {
    if (!join.actual_joining_date || !join.employee_id || !join.reporting_manager) {
      return toast.error("All join fields are required");
    }
    call("joined", join);
  };

  return (
    <div className="mt-3 flex flex-wrap gap-2">
      {c.status === "offer_extended" && (
        <>
          <button data-testid="mark-accepted-btn" onClick={() => call("accepted")} disabled={busy} className="bg-klein hover:bg-klein-hover text-white px-3 py-1.5 text-xs font-mono uppercase tracking-wider">
            Mark accepted
          </button>
          <button data-testid="mark-declined-btn" onClick={() => call("declined", {}, "Mark this offer as declined?")} disabled={busy} className="border border-error text-error hover:bg-[#FEF2F2] px-3 py-1.5 text-xs font-mono uppercase tracking-wider">
            Mark declined
          </button>
        </>
      )}
      {c.status === "offer_accepted" && !showJoin && (
        <>
          <button data-testid="open-join-form" onClick={() => setShowJoin(true)} className="bg-klein hover:bg-klein-hover text-white px-3 py-1.5 text-xs font-mono uppercase tracking-wider">
            Mark joined →
          </button>
          <button data-testid="mark-dropped-btn" onClick={() => call("dropped", {}, "Mark this offer as dropped?")} disabled={busy} className="border border-error text-error hover:bg-[#FEF2F2] px-3 py-1.5 text-xs font-mono uppercase tracking-wider">
            Mark dropped
          </button>
        </>
      )}
      {c.status === "offer_accepted" && showJoin && (
        <div className="w-full mt-2 bg-paper border border-rule p-3 grid grid-cols-3 gap-2" data-testid="join-form">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-wider text-quiet mb-1">Joining date *</div>
            <input data-testid="join-date" type="date" value={join.actual_joining_date} onChange={(e) => setJoin({ ...join, actual_joining_date: e.target.value })} className="w-full border border-rule px-2 py-1.5 text-sm bg-white" />
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-wider text-quiet mb-1">Employee ID *</div>
            <input data-testid="join-empid" value={join.employee_id} onChange={(e) => setJoin({ ...join, employee_id: e.target.value })} className="w-full border border-rule px-2 py-1.5 text-sm bg-white" />
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-wider text-quiet mb-1">Reporting manager *</div>
            <input data-testid="join-manager" value={join.reporting_manager} onChange={(e) => setJoin({ ...join, reporting_manager: e.target.value })} className="w-full border border-rule px-2 py-1.5 text-sm bg-white" />
          </div>
          <div className="col-span-3 flex gap-2">
            <button data-testid="confirm-join" disabled={busy} onClick={submitJoin} className="bg-ink text-white px-3 py-1.5 text-xs font-mono uppercase tracking-wider">
              Confirm join
            </button>
            <button onClick={() => setShowJoin(false)} className="border border-rule text-quiet px-3 py-1.5 text-xs font-mono uppercase tracking-wider">
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
